import { Language } from '../store/languageStore';

interface Translations {
  signIn: {
    title: string;
    subtitle: string;
    username: string;
    password: string;
    button: string;
    usernameRequired: string;
    passwordRequired: string;
  };
  timeTracker: {
    title: string;
    currentStatus: string;
    status: {
      notClockedIn: string;
      working: string;
      jobFinished: string;
      completed: string;
      inProgress: string;
    };
    time: {
      clockInTime: string;
      jobFinishTime: string;
      clockOutTime: string;
      duration: string;
      elapsed: string;
      totalDuration: string;
      started: string;
      ended: string;
    };
    actions: {
      clockIn: string;
      finishJob: string;
      clockOut: string;
      viewTasks: string;
      stopTask: string;
      completeActiveTasks: string;
    };
    confirmations: {
      clockIn: {
        title: string;
        message: string;
      };
      finishJob: {
        title: string;
        message: string;
      };
      clockOut: {
        title: string;
        message: string;
      };
      task: {
        startTitle: string;
        startMessage: string;
        endTitle: string;
        endMessage: string;
      };
    };
    tasks: {
      title: string;
      noTasks: string;
      activeTasks: string;
      taskStarted: string;
      taskDuration: string;
      emptyFolder: string;
    };
    offline: {
      message: string;
      limitedFeatures: string;
    };
    buttons: {
      confirm: string;
      cancel: string;
      processing: string;
    };
  };
  dashboard: {
    welcome: string;
    overview: string;
    metrics: {
      activeOrders: string;
      completedTasks: string;
      pendingTasks: string;
      recentExpenses: string;
    };
    taskSummary: {
      title: string;
      recentlyCompleted: string;
      pendingTasks: string;
      completionRate: string;
      noTasks: string;
    };
    recentActivity: {
      expenses: {
        title: string;
        noExpenses: string;
      };
      shifts: {
        title: string;
        noShifts: string;
        inProgress: string;
        ended: string;
      };
    };
    offline: {
      message: string;
    };
  };
  menu: {
    home: string;
    clock: string;
    orders: string;
    expenses: string;
    shifts: string;
  };
  expenses: {
    title: string;
    addExpense: string;
    noExpenses: string;
    total: string;
    spendings: string;
    penalties: string;
    form: {
      title: string;
      date: string;
      category: string;
      selectCategory: string;
      workOrder: string;
      selectWorkOrder: string;
      expenseType: string;
      spending: string;
      penalty: string;
      amount: string;
      description: string;
      attachments: string;
      uploadFile: string;
      maxFileSize: string;
      supportedFormats: string;
    };
    status: {
      offline: string;
      syncing: string;
      syncSuccess: string;
      syncError: string;
    };
    buttons: {
      add: string;
      cancel: string;
      adding: string;
    };
  };
  languages: {
    en: string;
    ru: string;
    uz: string;
  };
}

const translations: Record<Language, Translations> = {
  en: {
    signIn: {
      title: 'Worker\'s Ponel',
      subtitle: 'Sign in to your account',
      username: 'Username',
      password: 'Password',
      button: 'Sign In',
      usernameRequired: 'Username is required',
      passwordRequired: 'Password is required'
    },
    timeTracker: {
      title: 'Time Tracker',
      currentStatus: 'Current Status',
      status: {
        notClockedIn: 'Not Clocked In',
        working: 'Working',
        jobFinished: 'Job Finished',
        completed: 'Completed',
        inProgress: 'In Progress'
      },
      time: {
        clockInTime: 'Clock In Time',
        jobFinishTime: 'Job Finish Time',
        clockOutTime: 'Clock Out Time',
        duration: 'Duration',
        elapsed: 'Elapsed',
        totalDuration: 'Total Duration',
        started: 'Started',
        ended: 'Ended'
      },
      actions: {
        clockIn: 'Clock In',
        finishJob: 'Finish Job',
        clockOut: 'Clock Out',
        viewTasks: 'View Tasks',
        stopTask: 'Stop Task',
        completeActiveTasks: 'Complete Active Tasks First'
      },
      confirmations: {
        clockIn: {
          title: 'Clock In',
          message: 'Are you sure you want to clock in? This will begin tracking your time.'
        },
        finishJob: {
          title: 'Finish Job',
          message: 'Are you sure you want to finish this job?'
        },
        clockOut: {
          title: 'Clock Out',
          message: 'Are you sure you want to clock out?'
        },
        task: {
          startTitle: 'Start Task',
          startMessage: 'Are you sure you want to start this task? This will begin tracking your time.',
          endTitle: 'Complete Task',
          endMessage: 'Are you sure you want to complete this task? This will stop the timer and submit your work.'
        }
      },
      tasks: {
        title: 'Active Tasks',
        noTasks: 'No active tasks',
        activeTasks: 'Active Tasks ({count})',
        taskStarted: 'Task started successfully',
        taskDuration: 'Task must run for at least 10 seconds',
        emptyFolder: 'This folder is empty'
      },
      offline: {
        message: 'You are currently offline.',
        limitedFeatures: 'Some features may be limited.'
      },
      buttons: {
        confirm: 'Confirm',
        cancel: 'Cancel',
        processing: 'Processing...'
      }
    },
    dashboard: {
      welcome: 'Welcome back, {name}! 👋',
      overview: 'Here\'s an overview of your recent activity',
      metrics: {
        activeOrders: 'Active Orders',
        completedTasks: 'Completed Tasks',
        pendingTasks: 'Pending Tasks',
        recentExpenses: 'Recent Expenses'
      },
      taskSummary: {
        title: 'Task Summary',
        recentlyCompleted: 'Recently Completed',
        pendingTasks: 'Pending Tasks',
        completionRate: 'Completion Rate',
        noTasks: 'No tasks available'
      },
      recentActivity: {
        expenses: {
          title: 'Recent Expenses',
          noExpenses: 'No recent expenses'
        },
        shifts: {
          title: 'Recent Shifts',
          noShifts: 'No recent shifts',
          inProgress: 'In Progress',
          ended: 'Ended'
        }
      },
      offline: {
        message: 'You are currently offline. Some features may be limited.'
      }
    },
    menu: {
      home: 'Home',
      clock: 'Clock',
      orders: 'Orders',
      expenses: 'Expenses',
      shifts: 'Shifts'
    },
    expenses: {
      title: 'Expenses',
      addExpense: 'Add Expense',
      noExpenses: 'No Expense found',
      total: 'Total',
      spendings: 'Spendings',
      penalties: 'Penalties',
      form: {
        title: 'Add New Expense',
        date: 'Date',
        category: 'Expense Category',
        selectCategory: 'Select Category',
        workOrder: 'Work Order',
        selectWorkOrder: 'Select Work Order',
        expenseType: 'Expense Type',
        spending: 'Spending',
        penalty: 'Penalty',
        amount: 'Amount ($)',
        description: 'Description',
        attachments: 'Attachments',
        uploadFile: 'Click to upload file',
        maxFileSize: 'Max file size: 10MB per file. Up to 5 files.',
        supportedFormats: 'Supported formats: Images, PDF, DOC, DOCX, XLS, XLSX'
      },
      status: {
        offline: 'You are currently offline. Changes will sync when back online.',
        syncing: 'Syncing...',
        syncSuccess: 'Expense added successfully',
        syncError: 'Failed to add expense'
      },
      buttons: {
        add: 'Add Expense',
        cancel: 'Cancel',
        adding: 'Adding...'
      }
    },
    languages: {
      en: 'English',
      ru: 'Russian',
      uz: 'Uzbek'
    }
  },
  ru: {
    signIn: {
      title: 'Worker\'s Ponel',
      subtitle: 'Войдите в свой аккаунт',
      username: 'Имя пользователя',
      password: 'Пароль',
      button: 'Войти',
      usernameRequired: 'Введите имя пользователя',
      passwordRequired: 'Введите пароль'
    },
    timeTracker: {
      title: 'Учет времени',
      currentStatus: 'Текущий статус',
      status: {
        notClockedIn: 'Не на смене',
        working: 'В работе',
        jobFinished: 'Работа завершена',
        completed: 'Выполнено',
        inProgress: 'В процессе'
      },
      time: {
        clockInTime: 'Время начала',
        jobFinishTime: 'Время завершения работы',
        clockOutTime: 'Время окончания',
        duration: 'Длительность',
        elapsed: 'Прошло',
        totalDuration: 'Общая длительность',
        started: 'Начало',
        ended: 'Окончание'
      },
      actions: {
        clockIn: 'Начать смену',
        finishJob: 'Завершить работу',
        clockOut: 'Закончить смену',
        viewTasks: 'Просмотр задач',
        stopTask: 'Остановить задачу',
        completeActiveTasks: 'Сначала завершите активные задачи'
      },
      confirmations: {
        clockIn: {
          title: 'Начать смену',
          message: 'Вы уверены, что хотите начать смену? Это начнет отслеживание вашего времени.'
        },
        finishJob: {
          title: 'Завершить работу',
          message: 'Вы уверены, что хотите завершить эту работу?'
        },
        clockOut: {
          title: 'Закончить смену',
          message: 'Вы уверены, что хотите закончить смену?'
        },
        task: {
          startTitle: 'Начать задачу',
          startMessage: 'Вы уверены, что хотите начать эту задачу? Это начнет отслеживание времени.',
          endTitle: 'Завершить задачу',
          endMessage: 'Вы уверены, что хотите завершить эту задачу? Это остановит таймер и отправит вашу работу.'
        }
      },
      tasks: {
        title: 'Активные задачи',
        noTasks: 'Нет активных задач',
        activeTasks: 'Активные задачи ({count})',
        taskStarted: 'Задача успешно начата',
        taskDuration: 'Задача должна выполняться не менее 10 секунд',
        emptyFolder: 'Эта папка пуста'
      },
      offline: {
        message: 'Вы сейчас не в сети.',
        limitedFeatures: 'Некоторые функции могут быть ограничены.'
      },
      buttons: {
        confirm: 'Подтвердить',
        cancel: 'Отмена',
        processing: 'Обработка...'
      }
    },
    dashboard: {
      welcome: 'С возвращением, {name}! 👋',
      overview: 'Обзор вашей недавней активности',
      metrics: {
        activeOrders: 'Активные заказы',
        completedTasks: 'Выполненные задачи',
        pendingTasks: 'Ожидающие задачи',
        recentExpenses: 'Недавние расходы'
      },
      taskSummary: {
        title: 'Сводка задач',
        recentlyCompleted: 'Недавно выполненные',
        pendingTasks: 'Ожидающие задачи',
        completionRate: 'Процент выполнения',
        noTasks: 'Нет доступных задач'
      },
      recentActivity: {
        expenses: {
          title: 'Недавние расходы',
          noExpenses: 'Нет недавних расходов'
        },
        shifts: {
          title: 'Недавние смены',
          noShifts: 'Нет недавних смен',
          inProgress: 'В процессе',
          ended: 'Завершено'
        }
      },
      offline: {
        message: 'Вы сейчас не в сети. Некоторые функции могут быть ограничены.'
      }
    },
    menu: {
      home: 'Главная',
      clock: 'Время',
      orders: 'Заказы',
      expenses: 'Расходы',
      shifts: 'Смены'
    },
    expenses: {
      title: 'Расходы',
      addExpense: 'Добавить расход',
      noExpenses: 'Расходы не найдены',
      total: 'Всего',
      spendings: 'Траты',
      penalties: 'Штрафы',
      form: {
        title: 'Добавить новый расход',
        date: 'Дата',
        category: 'Категория расхода',
        selectCategory: 'Выберите категорию',
        workOrder: 'Рабочий заказ',
        selectWorkOrder: 'Выберите заказ',
        expenseType: 'Тип расхода',
        spending: 'Трата',
        penalty: 'Штраф',
        amount: 'Сумма ($)',
        description: 'Описание',
        attachments: 'Вложения',
        uploadFile: 'Нажмите для загрузки файла',
        maxFileSize: 'Максимальный размер файла: 10МБ. До 5 файлов.',
        supportedFormats: 'Поддерживаемые форматы: Изображения, PDF, DOC, DOCX, XLS, XLSX'
      },
      status: {
        offline: 'Вы не в сети. Изменения синхронизируются при подключении.',
        syncing: 'Синхронизация...',
        syncSuccess: 'Расход успешно добавлен',
        syncError: 'Не удалось добавить расход'
      },
      buttons: {
        add: 'Добавить расход',
        cancel: 'Отмена',
        adding: 'Добавление...'
      }
    },
    languages: {
      en: 'Английский',
      ru: 'Русский',
      uz: 'Узбекский'
    }
  },
  uz: {
    signIn: {
      title: 'Worker\'s Ponel',
      subtitle: 'Hisobingizga kiring',
      username: 'Foydalanuvchi nomi',
      password: 'Parol',
      button: 'Kirish',
      usernameRequired: 'Foydalanuvchi nomini kiriting',
      passwordRequired: 'Parolni kiriting'
    },
    timeTracker: {
      title: 'Vaqt hisobi',
      currentStatus: 'Joriy holat',
      status: {
        notClockedIn: 'Smenada emas',
        working: 'Ishlayapti',
        jobFinished: 'Ish tugadi',
        completed: 'Bajarildi',
        inProgress: 'Davom etmoqda'
      },
      time: {
        clockInTime: 'Boshlash vaqti',
        jobFinishTime: 'Ishni tugatish vaqti',
        clockOutTime: 'Tugatish vaqti',
        duration: 'Davomiyligi',
        elapsed: "O'tgan vaqt",
        totalDuration: 'Umumiy davomiyligi',
        started: 'Boshlandi',
        ended: 'Tugadi'
      },
      actions: {
        clockIn: 'Smenani boshlash',
        finishJob: 'Ishni tugatish',
        clockOut: 'Smenani tugatish',
        viewTasks: 'Vazifalarni ko\'rish',
        stopTask: 'Vazifani to\'xtatish',
        completeActiveTasks: 'Avval faol vazifalarni tugating'
      },
      confirmations: {
        clockIn: {
          title: 'Smenani boshlash',
          message: 'Smenani boshlashni xohlaysizmi? Bu vaqtingizni kuzatishni boshlaydi.'
        },
        finishJob: {
          title: 'Ishni tugatish',
          message: 'Bu ishni tugatishni xohlaysizmi?'
        },
        clockOut: {
          title: 'Smenani tugatish',
          message: 'Smenani tugatishni xohlaysizmi?'
        },
        task: {
          startTitle: 'Vazifani boshlash',
          startMessage: 'Bu vazifani boshlashni xohlaysizmi? Bu vaqtni kuzatishni boshlaydi.',
          endTitle: 'Vazifani tugatish',
          endMessage: 'Bu vazifani tugatishni xohlaysizmi? Bu taymerni to\'xtatadi va ishingizni yuboradi.'
        }
      },
      tasks: {
        title: 'Faol vazifalar',
        noTasks: 'Faol vazifalar yo\'q',
        activeTasks: 'Faol vazifalar ({count})',
        taskStarted: 'Vazifa muvaffaqiyatli boshlandi',
        taskDuration: 'Vazifa kamida 10 soniya davom etishi kerak',
        emptyFolder: 'Bu papka bo\'sh'
      },
      offline: {
        message: 'Siz hozir oflayndasiz.',
        limitedFeatures: 'Ba\'zi funksiyalar cheklangan bo\'lishi mumkin.'
      },
      buttons: {
        confirm: 'Tasdiqlash',
        cancel: 'Bekor qilish',
        processing: 'Ishlov berilmoqda...'
      }
    },
    dashboard: {
      welcome: 'Xush kelibsiz, {name}! 👋',
      overview: 'Sizning so\'nggi faoliyatingiz haqida ma\'lumot',
      metrics: {
        activeOrders: 'Faol buyurtmalar',
        completedTasks: 'Bajarilgan vazifalar',
        pendingTasks: 'Kutilayotgan vazifalar',
        recentExpenses: 'So\'nggi xarajatlar'
      },
      taskSummary: {
        title: 'Vazifalar xulosasi',
        recentlyCompleted: 'Yaqinda bajarilgan',
        pendingTasks: 'Kutilayotgan vazifalar',
        completionRate: 'Bajarish darajasi',
        noTasks: 'Vazifalar mavjud emas'
      },
      recentActivity: {
        expenses: {
          title: 'So\'nggi xarajatlar',
          noExpenses: 'So\'nggi xarajatlar yo\'q'
        },
        shifts: {
          title: 'So\'nggi smenalar',
          noShifts: 'So\'nggi smenalar yo\'q',
          inProgress: 'Davom etmoqda',
          ended: 'Tugatilgan'
        }
      },
      offline: {
        message: 'Siz hozir oflayndasiz. Ba\'zi funksiyalar cheklangan bo\'lishi mumkin.'
      }
    },
    menu: {
      home: 'Asosiy',
      clock: 'Vaqt',
      orders: 'Buyurtma',
      expenses: 'Xarajat',
      shifts: 'Smena'
    },
    expenses: {
      title: 'Xarajatlar',
      addExpense: 'Xarajat qo\'shish',
      noExpenses: 'Xarajatlar topilmadi',
      total: 'Jami',
      spendings: 'Xarajatlar',
      penalties: 'Jarimalar',
      form: {
        title: 'Yangi xarajat qo\'shish',
        date: 'Sana',
        category: 'Xarajat toifasi',
        selectCategory: 'Toifani tanlang',
        workOrder: 'Ish buyurtmasi',
        selectWorkOrder: 'Buyurtmani tanlang',
        expenseType: 'Xarajat turi',
        spending: 'Xarajat',
        penalty: 'Jarima',
        amount: 'Miqdor ($)',
        description: 'Tavsif',
        attachments: 'Fayllar',
        uploadFile: 'Fayl yuklash uchun bosing',
        maxFileSize: 'Maksimal fayl hajmi: 10MB. 5 tagacha fayl.',
        supportedFormats: 'Qo\'llab-quvvatlanadigan formatlar: Rasmlar, PDF, DOC, DOCX, XLS, XLSX'
      },
      status: {
        offline: 'Siz oflayndasiz. O\'zgarishlar onlayn bo\'lganda sinxronlanadi.',
        syncing: 'Sinxronlanmoqda...',
        syncSuccess: 'Xarajat muvaffaqiyatli qo\'shildi',
        syncError: 'Xarajat qo\'shib bo\'lmadi'
      },
      buttons: {
        add: 'Xarajat qo\'shish',
        cancel: 'Bekor qilish',
        adding: 'Qo\'shilmoqda...'
      }
    },
    languages: {
      en: 'Inglizcha',
      ru: 'Ruscha',
      uz: 'O\'zbekcha'
    }
  }
};

export default translations;